import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  Container, 
  IconButton, 
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Divider
} from '@mui/material';
import { Close, LocationOn, WorkOutline } from '@mui/icons-material';

const Careers = ({ open, onClose }) => {
  const openPositions = [
    {
      title: 'Senior Frontend Developer',
      department: 'Engineering',
      location: 'Remote (US)',
      description: 'We are looking for an experienced frontend developer with strong React skills to help build and improve our user interfaces.'
    },
    {
      title: 'Machine Learning Engineer',
      department: 'AI Team',
      location: 'San Francisco, CA',
      description: 'Join our AI team to develop and optimize machine learning models for our advanced search engine.'
    },
    {
      title: 'Product Marketing Manager',
      department: 'Marketing',
      location: 'Remote (Worldwide)',
      description: 'Help us shape and communicate our product story to customers and prospects.'
    }
  ];

  const benefits = [
    'Competitive salary and equity',
    'Health, dental, and vision insurance',
    'Flexible work hours and location',
    'Professional development budget',
    'Regular team retreats',
    '401(k) matching'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" color="#0d9488">
            Careers at Searcheble
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Container>
          <Typography variant="h6" gutterBottom>
            Join Our Team
          </Typography>
          <Typography paragraph>
            At Searcheble, we're building the future of knowledge discovery and management. Our team consists of passionate 
            individuals who are dedicated to creating technology that makes information more accessible and useful.
          </Typography>
          
          <Typography paragraph>
            We value diversity of thought and background, and we're looking for people who can bring new perspectives 
            to our team. If you're excited about solving complex problems and making a meaningful impact, we'd love to hear from you.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Our Culture
          </Typography>
          <Typography paragraph>
            We believe in a collaborative, transparent, and flexible work environment. Our team is distributed across 
            multiple time zones, and we prioritize clear communication and documentation. We trust our team members 
            to do their best work, wherever and whenever that may be.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Benefits & Perks
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#0d9488',
                    display: 'inline-block',
                    mr: 1.5
                  }}/>
                  <Typography>{benefit}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom>
            Open Positions
          </Typography>
          <Grid container spacing={3}>
            {openPositions.map((position, index) => (
              <Grid item xs={12} key={index}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {position.title}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
                        <WorkOutline fontSize="small" sx={{ color: "#0d9488", mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {position.department}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn fontSize="small" sx={{ color: "#0d9488", mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {position.location}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography paragraph variant="body2">
                      {position.description}
                    </Typography>
                    
                    <Button 
                      variant="outlined" 
                      sx={{ 
                        color: '#0d9488', 
                        borderColor: '#0d9488',
                        '&:hover': {
                          borderColor: '#0d9488',
                          backgroundColor: 'rgba(13, 148, 136, 0.1)'
                        }
                      }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Don't see a position that fits your skills?
            </Typography>
            <Typography paragraph>
              We're always looking for talented individuals to join our team.
            </Typography>
            <Button
              variant="contained"
              sx={{
                bgcolor: '#0d9488',
                '&:hover': { bgcolor: 'rgba(13, 148, 136, 0.8)' }
              }}
            >
              Send Your Resume
            </Button>
          </Box>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default Careers;